import type { FC, ReactNode, RefObject } from 'react'
import type { ActivityRow } from '@/constants/home'

import { useEffect, useRef, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/lib/format'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { Reveal } from '@/components/generals/reveal'
import { ButtonLink } from '@/components/generals/button'
import { LiveDot } from '@/components/generals/live-dot'
import { RollingNumber } from '@/components/generals/rolling-number'
import { SectionHeading } from '@/components/generals/section-heading'
import { activityFeed, annualFigures } from '@/constants/home'

interface FeedRowProps {
    row: ActivityRow
    root: RefObject<HTMLDivElement | null>
}

const FeedRow: FC<FeedRowProps> = ({ row, root }): ReactNode => {
    const ref = useRef<HTMLLIElement>(null)
    const active = useInView(ref, { root, threshold: 0.6 })

    return (
        <li ref={ref} className='flex items-center gap-4 px-5 py-3.5 sm:gap-7 sm:px-8 sm:py-4'>
            <span className='headline min-w-[1.9em] shrink-0 text-[34px] leading-none text-ink sm:text-[54px]'>
                <RollingNumber value={formatNumber(row.value)} active={active} duration={1400} />
            </span>

            <span className='min-w-0 flex-1 text-[15px] leading-snug text-ink-soft sm:text-[17px]'>
                {row.label}
            </span>

            <span className='eyebrow shrink-0 text-stone'>
                {row.time}
            </span>
        </li>
    )
}

/** Live activity rows that slowly scroll upward in a loop. Numbers roll as rows arrive and refresh. */
const ActivityFeed: FC = (): ReactNode => {
    const containerRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const copyRef = useRef<HTMLUListElement>(null)
    const hovering = useRef(false)
    const reduced = useReducedMotion()
    const visible = useInView(containerRef, { threshold: 0.1 })
    const [rows, setRows] = useState<Array<ActivityRow>>(activityFeed)

    useEffect(() => {
        const container = containerRef.current
        const list = listRef.current
        const copy = copyRef.current

        if (!container || !list || !copy) return

        if (reduced) {
            list.style.transform = ''
            return
        }

        let height = copy.offsetHeight
        let offset = 0
        let speed = 18
        let last = performance.now()
        let frame = 0
        let shown = false

        const tick = (now: number) => {
            frame = 0

            if (!shown) return

            const delta = Math.min(0.05, (now - last) / 1000)
            const target = hovering.current ? 4 : 18

            last = now
            speed += (target - speed) * Math.min(1, delta * 4)
            offset -= speed * delta

            if (height > 0 && offset <= -height) offset += height

            list.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`
            frame = requestAnimationFrame(tick)
        }

        const resize = new ResizeObserver(() => {
            height = copy.offsetHeight
        })

        const observer = new IntersectionObserver(([entry]) => {
            shown = entry.isIntersecting

            if (shown && !frame) {
                last = performance.now()
                frame = requestAnimationFrame(tick)
            }
        })

        resize.observe(copy)
        observer.observe(container)

        return () => {
            cancelAnimationFrame(frame)
            resize.disconnect()
            observer.disconnect()
        }
    }, [reduced])

    useEffect(() => {
        if (!visible || reduced) return

        const timer = window.setInterval(() => {
            setRows(current => {
                const index = Math.floor(Math.random() * current.length)
                const next = [...current]

                next[index] = { ...next[index], value: next[index].value + 1 + Math.floor(Math.random() * 4) }

                return next
            })
        }, 4200)

        return () => window.clearInterval(timer)
    }, [visible, reduced])

    return (
        <div
            className='surface overflow-hidden rounded-[24px] sm:rounded-[28px]'
            onMouseEnter={() => { hovering.current = true }}
            onMouseLeave={() => { hovering.current = false }}
        >
            <div className='flex items-center justify-between gap-4 px-5 pb-2 pt-5 sm:px-8 sm:pt-6'>
                <span className='eyebrow flex items-center gap-2.5 text-ink-soft'>
                    <LiveDot />
                    Live activity
                </span>

                <span className='eyebrow text-stone'>
                    Across all flows
                </span>
            </div>

            <div
                ref={containerRef}
                className={cn('relative overflow-hidden', !reduced && 'mask-fade-y h-[380px] sm:h-[500px]')}
            >
                <div ref={listRef}>
                    <ul ref={copyRef} aria-label='Recent activity across Quayside flows'>
                        {rows.map(row => (
                            <FeedRow key={row.label} row={row} root={containerRef} />
                        ))}
                    </ul>

                    {!reduced && (
                        <ul aria-hidden='true'>
                            {rows.map(row => (
                                <FeedRow key={row.label} row={row} root={containerRef} />
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    )
}

/** Tall card with four huge rolling counters. Events processed keeps ticking while on screen. */
const AnnualFigures: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { threshold: 0.3 })
    const reduced = useReducedMotion()
    const [events, setEvents] = useState(1846203517)

    useEffect(() => {
        if (!inView || reduced) return

        const timer = window.setInterval(() => {
            setEvents(value => value + 18 + Math.floor(Math.random() * 60))
        }, 2600)

        return () => window.clearInterval(timer)
    }, [inView, reduced])

    const figures = annualFigures.map((figure, index) => index === 0 ? { ...figure, value: formatNumber(events) } : figure)

    return (
        <div ref={ref} className='surface flex h-full flex-col rounded-[24px] p-4 sm:rounded-[28px] sm:p-8'>
            <span className='eyebrow text-ink-soft'>
                Annual figures
            </span>

            <dl className='mt-5 flex flex-1 flex-col gap-3'>
                {figures.map((figure, index) => (
                    <div
                        key={figure.label}
                        className={cn(
                            'flex flex-1 flex-col justify-center rounded-[18px] px-4 py-4 sm:rounded-[20px] sm:px-5 sm:py-5',
                            index === 0 ? 'bg-ink text-white' : 'bg-cream text-ink'
                        )}
                    >
                        <dt className={cn('eyebrow flex items-center justify-between gap-3', index === 0 ? 'text-white/55' : 'text-stone')}>
                            {figure.label}

                            {index === 0 && (
                                <span className='flex items-center gap-2 text-white/70'>
                                    <LiveDot />
                                    Live
                                </span>
                            )}
                        </dt>

                        <dd className='headline mt-3 text-[clamp(1.6rem,7.6vw,3.25rem)] leading-none lg:text-[clamp(1.75rem,2.7vw,2.75rem)]'>
                            <RollingNumber value={figure.value} active={inView} />
                        </dd>
                    </div>
                ))}
            </dl>
        </div>
    )
}

export const Numbers: FC = (): ReactNode => {
    return (
        <section id='numbers' aria-labelledby='numbers-title' className='section-y relative'>
            <div className='shell'>
                <SectionHeading
                    id='numbers-title'
                    eyebrow="Live production"
                    title="The numbers speak"
                    accent="for themselves."
                    intro="Behind every update is a real production flow, moving freight data between systems and keeping operations running every minute."
                    action={(
                        <ButtonLink href='/contact'>
                            Book a demo
                            <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/btn:translate-x-0.5' />
                        </ButtonLink>
                    )}
                />

                <div className='mt-12 grid gap-4 *:min-w-0 sm:gap-5 lg:mt-16 lg:grid-cols-12'>
                    <Reveal className='lg:col-span-7'>
                        <ActivityFeed />
                    </Reveal>

                    <Reveal delay={120} className='lg:col-span-5'>
                        <AnnualFigures />
                    </Reveal>
                </div>
            </div>
        </section>
    )
}
